<template>
    <div>
        <fullscreen-loading v-if="loading" />

        <div>
            <div class="mb-3 mt-3 d-flex align-items-center" v-if="communication === PLAYER_COMMUNICATION.WEBSOCKET || communication === PLAYER_COMMUNICATION.IPC">
                <h3 class="mb-0" v-if="ceremony">{{ ceremony.name }}</h3>

                <div class="ml-auto text-right">
                    <slot name="buttons-right"/>

                    <button class="btn btn-xs btn-primary ml-3"
                            @click="showStreamingStatus = true"
                            v-if="ceremony && canShowStreamingStatus"
                    >{{ $t('blocks.streaming') }}
                    </button>

                    <button class="btn btn-xs btn-warning ml-3"
                            @click="reset"
                            v-if="ceremony && blocksPlayed.length && !ceremony.is_processing && !isTypeLedWall() && communication !== PLAYER_COMMUNICATION.IPC"
                            v-tooltip="$t('blocks.reset_blocks', {type: ceremony.translated_type.toLowerCase()})"
                    >{{ $t('blocks.again') }}
                    </button>

                    <button class="btn btn-xs btn-company ml-3"
                            @click="playBlockItem(fixedBlock)"
                            v-if="ceremony && fixedBlock && !ceremony.is_processing"
                            v-tooltip="$t('blocks.click_here_to_use_fixed_photo')"
                    >{{ $t('blocks.show_fixed_photo') }}
                    </button>

                    <slot name="button-script" :ceremony="ceremony" v-if="ceremony && !ceremony.is_processing && !isTypeLedWall()">
                        <a :href="'/api/ceremonies/code/' + ceremony.code  + '/pdf-preview'"
                           target="_blank"
                           type="button"
                           class="btn btn-xs btn-company ml-3"
                           v-if="communication !== PLAYER_COMMUNICATION.IPC"
                           v-tooltip="$t('blocks.pdf_tooltip', {type: ceremony.translated_type.toLowerCase()})"
                        >{{ $t('ceremony.script') }}</a>
                    </slot>

                    <button class="btn btn-secondary btn-xs ml-3" @click="back" v-if="!isTypeLedWall()">
                        <icon name="arrow-left" class="mr-1"></icon>
                        {{ $t('general.back') }}
                    </button>
                </div>
            </div>
            <div v-else-if="ceremony && communication === PLAYER_COMMUNICATION.INLINE && fixedBlock && !ceremony.is_processing" class="mb-20 mb-3 text-right">
                <button class="btn btn-xs btn-company"
                        @click="playBlockItem(fixedBlock)"
                        v-tooltip="$t('blocks.click_here_to_use_fixed_photo')"
                >{{ $t('blocks.show_fixed_photo') }}
                </button>
            </div>

            <div class="col-12 mb-2" v-if="ceremonyDownloadStatus && (!ceremonyDownloadStatus?.hasCeremony || !ceremonyDownloadStatus?.hasBlocks)">
                <div class="alert alert-danger">
                    {{ $t('blocks.ceremony_not_found') }}
                </div>
            </div>

            <div v-if="ceremony">
                <div class="row" style="padding-bottom: 150px">
                    <div class="col-12 mb-2" v-if="!ceremony.cleaned_up_at && ceremony.is_processing">
                        <div class="alert alert-warning">
                            {{ $t('blocks.processing_files') }}
                        </div>
                    </div>

                    <div class="col-12 mb-2" v-if="!ceremony.cleaned_up_at && ceremonyDownloadStatus?.missingFiles?.length">
                        <div class="alert alert-warning">
                            {{ $t('blocks.block_content_missing_downloading') }}
                        </div>
                    </div>

                    <div class="col-12">
                        <div class="card controller-block-card" v-for="block in blocksToShow" :class="filterSubblocks(block).length <= 1 ? getBlockClass(block, 'card') : null">
                            <div class="card-body">
                                <div style="position: absolute; top: -10px; left: -10px;">
                                    <span v-if="block.block_type === 1" class="badge badge-info">{{ $t('blocks.photo') }}</span>
                                    <span v-if="block.block_type === 2" class="badge badge-primary">{{ $t('blocks.video') }}</span>
                                    <span v-if="block.block_type === 3" class="badge badge-dark">{{ $t('blocks.type_speaker') }}</span>
                                    <span v-if="block.block_type === 7" class="badge badge-dark">{{ $t('blocks.type_call') }}</span>
                                </div>

                                <div v-if="isBlockMissingFiles(block)" class="alert alert-warning d-flex align-items-center">
                                    <icon class="mr-2" name="exclamation-circle" />
                                    {{ $t('blocks.block_content_missing') }}
                                </div>

                                <div class="media align-items-center flex-wrap">
                                    <div>
                                        <div class="avatar-parent-child">
                                            <img :src="getBlockImage(block)" class="avatar rounded-circle">
                                            <span v-if="getBlockClass(block, 'card') === 'card-active'" class="avatar-child avatar-badge bg-success"></span>
                                        </div>
                                    </div>

                                    <block-info :class="'ml-4'" v-if="!isTypeLedWall()" :ceremony="ceremony" :block="block" :block-finished="getBlockClass(block, 'card') === 'card-finished'" />
                                    <div class="media-body" v-if="isTypeLedWall()"></div>

                                    <div class="d-flex action-buttons" v-if="!isTypeLedWall()">
                                        <button class="btn btn-sm btn-secondary" v-if="hasBlockDetails(block)" @click="blockDetails = filterSubblocks(block).length === 1 ? filterSubblocks(block)[0] : block">Details</button>

                                        <button v-if="canPlay(block) && communication !== PLAYER_COMMUNICATION.INLINE && ceremony.settings.play_without_transition" class="ml-3 d-flex align-items-center btn btn-sm btn-secondary" @click="playBlockItem(filterSubblocks(block).length ? filterSubblocks(block)[0] : block, 0, undefined, block.songs_override_id)">
                                            <img src="../../../../images/icons/play-no-transition.svg" style="height: 25px;" />
                                        </button>

                                        <button v-if="canPlay(block) && communication !== PLAYER_COMMUNICATION.INLINE && ceremony.settings.can_repeat_block" class="ml-3 d-flex align-items-center btn btn-sm btn-secondary" @click="playBlockItem(filterSubblocks(block).length ? filterSubblocks(block)[0] : block, undefined, true, block.songs_override_id)">
                                            <img src="../../../../images/icons/play-and-repeat.svg" style="height: 17px;" />
                                        </button>

                                        <button v-if="canPlay(block)" class="d-flex align-items-center btn btn-sm btn-secondary ml-2" @click="playBlockItem(filterSubblocks(block).length ? filterSubblocks(block)[0] : block, undefined, undefined, block.songs_override_id)">
                                            <icon name="play" style="margin-top: 2px;"></icon>
                                        </button>
                                    </div>
                                </div>

                                <div class="block-comment" v-if="block.comment">{{ block.comment }}</div>


                                <manual-controls
                                    v-if="(block.subblocks.length === 1 && block.subblocks[0].has_manual_action) || isTypeLedWall()"
                                    :block="block.subblocks[0]"
                                    :active-block-id="activeBlockId"
                                    :current-file-index="currentFileIndex"
                                    :code="ceremony.code"
                                    :can-play-block="canPlay(block)"
                                    :block-size="communication === PLAYER_COMMUNICATION.INLINE ? 'col-4 col-sm-4 col-xs-4 col-md-2 col-lg-2 mb-2' : 'col-4 col-sm-4 col-xs-4 col-md-2 col-lg-1 mb-2'"
                                    @show-file="showBlockFile(block.subblocks.length ? block.subblocks[0] : block, $event)"
                                />

                                <div v-for="subblock of filterSubblocks(block)" v-if="filterSubblocks(block).length > 1" class="subblock-preview" :class="getBlockClass(subblock, 'subblock')">
                                    <div class="media align-items-center flex-wrap">
                                        <div class="media-body">
                                            <span v-if="getBlockClass(subblock, 'subblock') === 'subblock-active'" class="subblock-active bg-success"></span>

                                            <block-info :ceremony="ceremony" :block="subblock" :block-finished="getBlockClass(subblock, 'subblock') === 'subblock-finished'" />
                                        </div>

                                        <div class="d-flex action-buttons">
                                            <button class="btn btn-sm btn-secondary" v-if="hasBlockDetails(subblock) && !isTypeLedWall()" @click="blockDetails = subblock">{{ $t('blocks.details') }}</button>

                                            <button v-if="canPlayBlock(subblock) && communication !== PLAYER_COMMUNICATION.INLINE && ceremony.settings.play_without_transition" class="ml-3 d-flex align-items-center btn btn-sm btn-secondary" @click="playBlockItem(subblock, 0)">
                                                <img src="../../../../images/icons/play-no-transition.svg" style="height: 25px;" />
                                            </button>

                                            <button v-if="canPlayBlock(subblock) && communication !== PLAYER_COMMUNICATION.INLINE && ceremony.settings.can_repeat_block" class="ml-3 d-flex align-items-center btn btn-sm btn-secondary" @click="playBlockItem(subblock, 0, true)">
                                                <img src="../../../../images/icons/play-and-repeat.svg" style="height: 17px;" />
                                            </button>

                                            <button class="btn btn-sm btn-secondary ml-2" @click="playBlockItem(subblock)" v-if="canPlayBlock(subblock)">
                                                <icon name="play" style="margin-top: 2px;"></icon>
                                            </button>
                                        </div>
                                    </div>

                                    <div class="block-comment" v-if="subblock.comment">{{ subblock.comment }}</div>

                                    <manual-controls
                                        v-if="subblock.has_manual_action"
                                        :block="subblock"
                                        :active-block-id="activeBlockId"
                                        :current-file-index="currentFileIndex"
                                        :code="ceremony.code"
                                        :can-play-block="canPlay(block)"
                                        :block-size="communication === PLAYER_COMMUNICATION.INLINE ? 'col-4 col-sm-4 col-xs-4 col-md-2 col-lg-2 mb-2' : 'col-4 col-sm-4 col-xs-4 col-md-2 col-lg-1 mb-2'"
                                        @show-file="showBlockFile(subblock, $event)"
                                    />
                                </div>

                                <div v-if="!isTypeLedWall() && playlistBlocks && playlistBlocks.length && communication !== PLAYER_COMMUNICATION.INLINE && (block.type === 5 || block.type === 6)">
                                    <label class="mt-3" for="music-playlist">{{ $t('blocks.select_music_from_playlist') }}</label>
                                    <select class="form-control form-control-filtering" id="music-playlist" v-model="block.songs_override_id" @change="changePlaylist(block.id, block.songs_override_id)">
                                        <option :value="block.id">{{ $t('blocks.from_supplied') }}</option>
                                        <option v-for="playlistBlock of playlistBlocks" :value="playlistBlock.id">{{ playlistBlock.name }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <modal :show="blockDetails" :closable="!previewFiles.length" @close="closeDetails" :custom_dialog_css="'width:80vw; max-width: 1000px;'">
                    <div slot="header">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t('blocks.details') }}</h5>
                            <button type="button" class="close" @click="closeDetails" aria-label="Close"><span aria-hidden="true">×</span></button>
                        </div>
                    </div>

                    <div slot="body" v-if="blockDetails && blockDetails.type === 7/*call*/">
                        <p>{{ $t('blocks.contact_with_survivor') }}</p>

                        <button
                            v-if="!livestream"
                            class="btn btn-sm btn-primary"
                            @click="setControllerLivestream(blockDetails.livestream)"
                        >
                            {{ $t('blocks.make_connection') }}
                        </button>

                        <div v-if="livestream">
                            <LiveVideoPlayer
                                :livestream="livestream"
                                :autoplay="true"
                                :playing="true"
                                :controls="false"
                                :fade-time="0"
                                :default-fade-class="'in'"
                                :token="'ceremony:' + livestream"
                            />
                        </div>
                    </div>

                    <div slot="body" v-if="blockDetails && blockDetails.type !== 7/*call*/">
                        <div class="mt-2 row">
                            <div class="col-12" v-if="!blockMedia.length && !blockAudio.length">
                                <div class="alert alert-info">
                                    {{ $t('blocks.block_has_no_media') }}
                                </div>
                            </div>


                            <div class="col-12" v-if="blockMedia.length">
                                <h6>
                                    {{ $t('blocks.media_in_block') }}
                                    <icon v-if="hasBlockRepeatingMedia(blockDetails) && !blockAudio.length" name="redo" scale="0.8" v-tooltip="$t('blocks.music_will_be_replayed')" />
                                </h6>
                            </div>

                            <div class="col-sm-4 col-xs-1 col-md-3 col-lg-2 mb-2" v-for="(file, index) of blockMedia" :key="index">
                                <file-block :file="file"
                                            :ceremony-code="ceremony.code"
                                            :show-actions="false"
                                            :show-warnings="false"
                                            @click="showFilePreview(blockMedia, file)"
                                />
                            </div>

                            <div class="col-12" v-if="blockAudio.length" :class="blockMedia.length ? 'mt-5' : null">
                                <h6>
                                    {{ $t('blocks.music_in_block') }}
                                    <icon v-if="hasBlockRepeatingAudio(blockDetails)" name="redo" scale="0.8" v-tooltip="$t('blocks.music_will_be_replayed')" />
                                </h6>
                            </div>

                            <div v-for="file of blockAudio" class="mb-4 col-12 col-sm-6">
                                <div class="d-flex d-inline-block align-items-center mb-2">
                                    <div
                                        class="icon icon-shape bg-soft-warning text-warning mr-2"
                                        style="background-size: cover"
                                        :style="{ backgroundImage: file.song.cover ? 'url(\'' + `/storage/song-images/${file.song.cover}` + '\')' : null}"
                                    >
                                        <icon v-if="!file.song.cover" name="music"></icon>
                                    </div>

                                    <div style="line-height: 13px;">
                                        {{ file.song.display_name || file.song.name }}
                                        <br />
                                        <div style="font-size:11px;" v-if="file.song.author">
                                            {{ file.song.author }}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div slot="footer">
                        <button
                            v-if="livestream"
                            class="btn btn-sm btn-primary mr-auto"
                            @click="setControllerLivestream(null)"
                        >
                            {{ $t('blocks.close_connection') }}
                        </button>

                        <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="closeDetails">{{ $t('general.close') }}</button>
                    </div>
                </modal>

                <modal :show="showBackConfirmation" @close="showBackConfirmation = null">
                    <div slot="header">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t('blocks.leave_type', { type: ceremony.translated_type }) }}</h5>
                            <button type="button" class="close" @click="showBackConfirmation = null" aria-label="Close"><span aria-hidden="true">×</span></button>
                        </div>
                    </div>

                    <div slot="body" v-if="ceremony">
                        <p>{{ $t('blocks.leave_confirmation', { type: ceremony.translated_type.toLowerCase() }) }}</p>
                    </div>

                    <div slot="footer">
                        <button type="button" class="btn btn-primary btn-sm" @click="confirmBack">{{ $t('blocks.back_to_dashboard') }}</button>
                        <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="showBackConfirmation = null">{{ $t('general.close') }}</button>
                    </div>
                </modal>

                <modal :show="ceremony.errors && ceremony.errors.length">
                    <div slot="header">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t('blocks.contains_errors', { type: ceremony.translated_type }) }}</h5>
                        </div>
                    </div>

                    <div slot="body" v-if="ceremony">
                        <div class="alert alert-danger">
                            <p>{{ $t('blocks.blocks_contain_errors', { type: ceremony.translated_type.toLowerCase() }) }}</p>
                            <strong>{{ $t('blocks.errors') }}:</strong>
                            <ul>
                                <li v-for="error of ceremony.errors">{{ error }}</li>
                            </ul>

                            <p>{{ $t('blocks.resolve_issues', { type: ceremony.translated_type.toLowerCase() }) }}</p>
                        </div>
                    </div>

                    <div slot="footer">
                        <button type="button" class="btn btn-primary btn-sm" @click="gotoBack">{{ $t('blocks.back_to_dashboard') }}</button>
                    </div>
                </modal>

                <modal :show="showStreamingStatus" @close="showStreamingStatus = false">
                    <div slot="header">
                        <div class="modal-header">
                            <h5 class="modal-title">{{ $t('blocks.streaming') }}</h5>
                            <button type="button" class="close" @click="showStreamingStatus = false" aria-label="Close"><span aria-hidden="true">×</span></button>
                        </div>
                    </div>

                    <div slot="body" v-if="showStreamingStatus">
                        <streaming-status :ceremony="ceremony" />
                    </div>

                    <div slot="footer">
                        <button type="button" class="btn btn-secondary btn-sm" data-dismiss="modal" @click="showStreamingStatus = false">{{ $t('general.close') }}</button>
                    </div>
                </modal>

                <fullscreen-slider :files="previewFiles" :file-index="previewFileIndex" :ceremony-code="ceremony.code" @clear="clearFilePreview" />

                <player-status
                    :current-duration="currentDuration"
                    :no-animation="loading || resetDuration"
                    :current-file-index="currentFileIndex"
                    :state="state"
                    :block="activeBlock"
                    :is-repeating="isRepeating"
                    :ceremony="ceremony"
                    :communication="communication"
                    :fixed-block="fixedBlock"
                    :current-volume="currentVolume"
                    v-if="withStatus && (!isTypeLedWall() || showStatus)"
                    @set-volume="setVolume"
                />
            </div>
        </div>
    </div>
</template>

<script>

    import FullscreenLoading from '../../components/FullscreenLoading';
    import Modal from '../../components/Modal';
    import SongPlayer from '../../components/SongPlayer';
    import FileBlock from '../../components/FileBlock';
    import FileMixin from '../../mixins/FileMixin';
    import FilePreviewMixin from '../../mixins/FilePreviewMixin';
    import BlockMixin from '../../mixins/BlockMixin';
    import FullscreenSlider from '../../components/FullscreenSlider';
    import PlayerStatus from '../../components/PlayerStatus';
    import BlockInfo from '../../components/BlockInfo';
    import ManualControls from '../../components/ManualControls';
    import { EventBus } from '../../utils/eventBus';
    import { getSettingByName } from '../../utils/duration';
    import * as Sentry from '@sentry/browser';
    import LiveVideoPlayer from '../../components/LiveVideoPlayer.vue';
    import { getMaxDurationFromBlock } from '../../utils/getMaxDurationForBlock';
    import StreamingStatus from '../../components/StreamingStatus.vue';
    import { PLAYER_COMMUNICATION } from '../../enums/player-communication';
    import { IpcCall, ipcCall } from '../../lite/utils/ipcCall';
    import { mapState } from 'vuex';

    export default {

        props: {
            ceremony_id: {
                type: Number | String,
                required: true,
            },
            withStatus: {
                type: Boolean,
                default: false,
            },
            communication: {
                type: String,
                default: PLAYER_COMMUNICATION.WEBSOCKET,
            },
        },

        components: {
            LiveVideoPlayer,
            FullscreenLoading,
            Modal,
            SongPlayer,
            FileBlock,
            FullscreenSlider,
            PlayerStatus,
            BlockInfo,
            ManualControls,
            StreamingStatus,
        },

        mixins: [
            FileMixin,
            FilePreviewMixin,
            BlockMixin,
        ],

        computed: {
            IpcCall() {
                return IpcCall
            },
            PLAYER_COMMUNICATION() {
                return PLAYER_COMMUNICATION;
            },

            activeBlock() {
                if (!this.ceremony) {
                    return null;
                }

                return this.getActiveBlock();
            },

            blockMedia() {
                if (!this.blockDetails) {
                    return [];
                }

                return this.getBlockMedia(this.blockDetails);
            },

            blockAudio() {
                if (!this.blockDetails) {
                    return [];
                }

                return this.getBlockAudio(this.blockDetails);
            },

            fixedBlock() {
                if (!this.ceremony) {
                    return [];
                }

                return this.ceremony.blocks.find(block => {
                    const isFixedBlock = block.settings.find(setting => setting.name === 'fixed_block' && setting.value);

                    return isFixedBlock && block.subblocks?.[0]?.files?.length;
                });
            },

            blocksToShow() {
                if (!this.ceremony) {
                    return [];
                }

                return this.ceremony.blocks.filter(block => {
                    const isFixedBlock = block.settings.find(setting => setting.name === 'fixed_block' && setting.value);

                    return !isFixedBlock;
                });
            },

            showStatus() {
                return this.$route.query.controls === 'true';
            },

            totalDuration() {
                return Number(parseInt(getMaxDurationFromBlock(this.ceremony, this.activeBlock, this.isRepeating)));
            },

            hasCeremonyStreaming() {
                return !!this.ceremony?.location?.livestream_channel_id;
            },

            canShowStreamingStatus() {
                return this.withStatus && (!this.isTypeLedWall() || this.showStatus) && this.hasCeremonyStreaming && this.companySettings?.show_streaming_status;
            },

            ...mapState({
                companySettings: state => state.companySettings,
            }),
        },

        beforeDestroy() {
            this.destroyEvents();
        },

        data() {
            return {
                ceremony: null,
                loading: false,

                blockDetails: null,
                livestream: null,

                ceremonyFinished: false,
                activeBlockId: null,
                blocksPlayed: [],
                state: null,
                currentDuration: 0,
                currentFileIndex: null,
                resetDuration: false,

                playlistBlocks: [],

                isRepeating: false,

                ceremonyUpdatedInterval: null,

                showBackConfirmation: false,
                showStreamingStatus: false,

                currentVolume: window.CURRENT_VOLUME || 100,
                ceremonyDownloadStatus: null,
            };
        },

        async mounted() {
            await this.getCeremony();

            if (!this.ceremony) {
                if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    this.ceremonyDownloadStatus = {
                        hasCeremony: false,
                    };
                } else {
                    this.$router.push('/');
                }

                return;
            }

            switch (this.communication) {
                case PLAYER_COMMUNICATION.WEBSOCKET:
                    await this.getCeremonyStatus();

                    EventBus.$on('WebsocketReconnect', () => {
                        console.log('Resetting websocket events');
                        this.setupWebsocketEvents();
                    });

                    this.setupWebsocketEvents();
                    break;
                case PLAYER_COMMUNICATION.INLINE:
                    this.setupInlineEvents();
                    break;
                case PLAYER_COMMUNICATION.IPC:
                    this.setupIpcEvents();
                    break;
            }

            if (this.communication === PLAYER_COMMUNICATION.WEBSOCKET) {
                // Every 60 seconds we want to know if the ceremony is updated
                this.ceremonyUpdatedInterval = setInterval(function() {
                    this.checkForCeremonyUpdate();
                }.bind(this), 60000);
            }
        },

        methods: {
            ipcCall,
            back() {
                if (this.ceremony && this.ceremony.settings.back_confirmation) {
                    this.showBackConfirmation = true;
                    return;
                }

                this.gotoBack();
            },

            async confirmBack() {
                this.loading = true;

                try {
                    if (this.communication === PLAYER_COMMUNICATION.WEBSOCKET) {
                        await this.stop();
                    }

                    if (this.communication === PLAYER_COMMUNICATION.IPC) {
                        await this.emitAction('BlockStateChanged', {
                            state: 'stopped',
                        });
                    }

                    this.gotoBack();

                    this.showBackConfirmation = false;
                } catch (exception) {

                }

                this.loading = false;
            },

            gotoBack() {
                this.$router.push({
                    name: 'ceremonies',
                });
            },

            canPlay(block) {
                if (this.isBlockMissingFiles(block)) {
                    return false;
                }

                const filteredSubblocks = this.filterSubblocks(block);

                return filteredSubblocks.length <= 1 && this.canPlayBlock(filteredSubblocks.length ? filteredSubblocks[0] : block);
            },

            getBlockClass(block, type) {
                if (this.isActiveBlock(block)) {
                    if (this.currentDuration < Number(block.total_duration) || this.isRepeating) {
                        return type + '-active';
                    }
                }

                if (this.isDone(block)) {
                    return type + '-finished';
                }

                return null;
            },

            isTypeLedWall() {
                return this.ceremony?.type === 'ledwall';
            },

            setupInlineEvents() {
                EventBus.$on('BlockStarted', this.blockStartedEvent);
                EventBus.$on('BlockFinished', this.blockFinishedEvent);
                EventBus.$on('BlockStateChanged', this.blockStateChangedEvent);
                EventBus.$on('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                EventBus.$on('BlockDurationChanged', this.blockDurationChangedEvent);
                EventBus.$on('CeremonyFinished', this.ceremonyFinishedEvent);
            },

            setupIpcEvents() {
                window.addEventListener('ipc-from-player', this.ipcFromPlayerEvents);
            },

            setupWebsocketEvents() {
                this.destroyEvents();

                const channel = Echo.channel('blockmessages');

                channel.listen('BlockStarted', this.blockStartedEvent);
                channel.listen('BlockFinished', this.blockFinishedEvent);
                channel.listen('BlockStateChanged', this.blockStateChangedEvent);
                channel.listen('BlockActiveFileChanged', this.blockActiveFileChangedEvent);
                channel.listen('BlockDurationChanged', this.blockDurationChangedEvent);
                channel.listen('CeremonyFinished', this.ceremonyFinishedEvent);
                channel.listen('CeremonyReset', this.ceremonyResetEvent);
                channel.listen('VolumeUpdated', this.volumeUpdated);
            },

            ipcFromPlayerEvents(event) {
                const detail = event.detail;

                switch (detail.action) {
                    case 'BlockStarted':
                        return this.blockStartedEvent(detail.data);
                    case 'BlockFinished':
                        return this.blockFinishedEvent(detail.data);
                    case 'BlockStateChanged':
                        return this.blockStateChangedEvent(detail.data);
                    case 'BlockActiveFileChanged':
                        return this.blockActiveFileChangedEvent(detail.data);
                    case 'BlockDurationChanged':
                        return this.blockDurationChangedEvent(detail.data);
                    case 'CeremonyFinished':
                        return this.ceremonyFinishedEvent(detail.data);
                }
            },

            destroyEvents() {
                if (this.communication === PLAYER_COMMUNICATION.WEBSOCKET) {
                    window.stopListening();
                }

                if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    window.removeEventListener('ipc-from-player', this.ipcFromPlayerEvents);
                }

                if (this.ceremonyUpdatedInterval) {
                    clearInterval(this.ceremonyUpdatedInterval);
                }
            },

            blockStartedEvent(event) {
                console.log('Started', event.block, event.state, event.should_repeat);

                this.state = event.state;
                this.activeBlockId = event.block.id;
                this.isRepeating = event.should_repeat;

                const activeBlockId = event.block.parent_block ? event.block.parent_block.id : event.block.id;

                this.changePlaylist(activeBlockId, event.block.songs_override_id || activeBlockId);

                this.changeDuration(0);
            },

            blockStateChangedEvent(event) {
                console.log('Changed state', event.state);

                if (event.state === 'duration') {
                    this.currentDuration = event.duration || 0;

                    return;
                }

                this.state = event.state;

                if (this.state === 'stopped') {
                    this.activeBlockId = null;
                    this.isRepeating = false;
                }
            },

            blockActiveFileChangedEvent(event) {
                console.log('Changed active file ', event.file.id);

                this.state = 'playing';
                this.activeBlockId = event.block.id;
                this.currentFileIndex = event.block.files.findIndex(file => file.id === event.file.id);
            },

            blockDurationChangedEvent(event) {
                const activeBlock = this.getActiveBlock();

                console.log('Changed duration', event.duration, event.max_duration);

                this.currentDuration = event.duration;
            },

            canShowFixedBlockAfterFinish(block) {
                const showFixedBlockAfterSpeakerFinish = block.ceremony?.company?.show_fixed_block_after_speaker_finish ?? true;

                if (showFixedBlockAfterSpeakerFinish) {
                    return true;
                }

                // All blocks start the fixed image except the speaker when the setting above is configured
                return (block.parent_block?.type || block.type) !== 3 /*Speaker*/;
            },

            blockFinishedEvent(event) {
                console.log('Finished', event.block);

                this.currentDuration = this.totalDuration;
                // When we stop we want to render the fixed block when configured.
                if ((this.communication === PLAYER_COMMUNICATION.INLINE || this.communication === PLAYER_COMMUNICATION.IPC) && !event.nextBlock && this.fixedBlock && this.fixedBlock.id !== event.block.id && this.canShowFixedBlockAfterFinish(event.block)) {
                    this.emitAction('BlockStarted', {
                        block: {
                            ...this.fixedBlock,
                            ceremony: this.ceremony,
                        },
                        state: 'playing',
                    });
                }

                const existing = this.blocksPlayed.find(blockPlayed => blockPlayed === event.block.id);

                if (existing) {
                    return;
                }

                this.blocksPlayed.push(event.block.id);
            },

            ceremonyFinishedEvent(event) {
                console.log('Ceremony is finished');

                this.activeBlockId = null;
                this.state = 'stopped';
                this.changeDuration(0);
            },

            ceremonyResetEvent(event) {
                console.log('Ceremony is resetted');

                this.activeBlockId = null;
                this.state = 'stopped';
                this.changeDuration(0);
                this.getCeremonyStatus();
            },

            volumeUpdated(event) {
                this.currentVolume = event.volume;
            },

            setVolume(volume) {
                this.currentVolume = volume;
                this.$http.post('/api/player/volume', {
                    volume,
                });
            },

            hasBlockRepeatingAudio(block) {
                const setting = getSettingByName(block.parent_block ? block.parent_block : block, 'repeat_audio');

                if (!setting) {
                    return false;
                }

                return setting.value === '1';
            },

            hasBlockRepeatingMedia(block) {
                const setting = getSettingByName(block, 'repeat_photos');

                if (!setting) {
                    return false;
                }

                return setting.value === '1';
            },

            changeDuration(duration) {
                this.currentDuration = duration;

                this.resetDuration = true;

                setTimeout(() => {
                    this.resetDuration = false;
                }, 100);
            },

            filterSubblocks(block) {
                return block.subblocks;
            },

            hasBlockDetails(block) {
                if (block.type === 7/*call*/) {
                    return !!block.livestream && this.communication !== PLAYER_COMMUNICATION.INLINE;
                }

                if (!block.parent_id && this.filterSubblocks(block).length <= 1) {
                    return true;
                }

                const media = this.getBlockMedia(block);
                const audio = this.getBlockAudio(block);

                return media.length || audio.length;
            },

            getBlockMedia(block) {
                if (!block.parent_id) {
                    if (block.subblocks.length <= 0 || block.type === 3) {
                        return [];
                    }

                    return block.subblocks[0].files.filter((file) => {
                        return this.isImage(file) || this.isVideo(file);
                    });
                }

                return block.files.filter((file) => {
                    return this.isImage(file) || this.isVideo(file);
                });
            },

            getBlockAudio(block) {
                const parentId = block.parent_id;
                let blockWithAudio = block;

                if (parentId) {
                    blockWithAudio = this.ceremony.blocks.find(blockToFind => blockToFind.id === parentId);
                }

                if (!blockWithAudio) {
                    return [];
                }

                return blockWithAudio.files.filter((file) => {
                    return this.isAudio(file);
                });
            },

            canPlayBlock(block) {
                const parent = block.parent_block;

                if (block.type === 7/*call*/ || (parent && parent.type === 7/*call*/)) {
                    return (!!block.livestream || (parent && !!parent.livestream)) && this.communication !== PLAYER_COMMUNICATION.INLINE;
                }

                if (parent) {
                    if (parent.type === 3/*3 = speaker*/ || parent.type === 5/*5 = inloop / uitloop*/ || parent.type === 6/*6 = condolence*/) {
                        return true;
                    }

                    return parent.files.length > 0 || block.files.length > 0;
                }

                const subBlock = block.subblocks.length ? block.subblocks[0] : block;

                return (
                    subBlock.files.length > 0 ||
                    block.files.length > 0 ||
                    block.type === 3/*3 = speaker*/ ||
                    block.type === 5/*5 = inloop / uitloop*/ ||
                    block.type === 6/*6 = condolence*/
                );
            },

            async getCeremony() {
                this.loading = true;

                try {
                    const { ceremony, blocks } = await this.getCeremonyData();

                    ceremony.blocks = blocks.map(block => {
                        block.songs_override_id = block.id;

                        return block;
                    });

                    this.playlistBlocks = ceremony.playlist_blocks || [];

                    this.ceremony = ceremony;

                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }

                this.loading = false;
            },

            async getCeremonyData() {
                if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    const ceremony = await ipcCall(IpcCall.GetCeremonyToControl, {
                        ceremonyId: this.ceremony_id,
                    });

                    this.ceremonyDownloadStatus = await ipcCall(IpcCall.CheckCeremonyDownloadStatus, {
                        ceremonyId: this.ceremony_id,
                    });

                    return ceremony;
                }

                let ceremonyUrl = '/api/ceremonies/' + this.ceremony_id;
                let blockUrl = '/api/player/ceremonies/' + this.ceremony_id + '/blocks';

                if (this.communication === PLAYER_COMMUNICATION.INLINE) {
                    ceremonyUrl = '/api/ceremonies/code/' + this.ceremony_id + '/player';
                    blockUrl = '/api/ceremonies/code/' + this.ceremony_id + '/player/blocks';
                }

                const response = await this.$http.get(ceremonyUrl);
                const blockResponse = await this.$http.get(blockUrl);

                return {
                    ceremony: response.data,
                    blocks: blockResponse.data,
                };
            },

            async getCeremonyStatus() {
                this.loading = true;

                try {
                    const response = await this.$http.get('/api/player/ceremonies/' + this.ceremony_id + '/status');

                    this.activeBlockId = response.data.active_block;
                    this.blocksPlayed = response.data.blocks_played;
                    this.ceremonyFinished = response.data.ceremony_finished;
                    this.state = response.data.state;
                    this.currentDuration = response.data.current_duration;
                    this.isRepeating = response.data.should_repeat;

                    const activeBlock = this.getActiveBlock();
                    const currentFileIndex = activeBlock ? activeBlock.files.findIndex(file => file.id === response.data.active_file_id) : null;

                    this.currentFileIndex = currentFileIndex !== -1 ? currentFileIndex : null;

                    if (response.data.songs_override_id) {
                        const activeBlock = this.getActiveBlock();

                        if (activeBlock) {
                            const activeBlockId = activeBlock.parent_block ? activeBlock.parent_block.id : activeBlock.id;

                            this.changePlaylist(activeBlockId, response.data.songs_override_id || activeBlockId);
                        }
                    }
                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }

                this.loading = false;
            },

            isDone(block) {
                if (this.ceremonyFinished) {
                    return true;
                }

                if (!block.parent_id) {
                    const subBlocks = this.filterSubblocks(block);//.filter(subblock => subblock.files.length > 0);

                    if (subBlocks.length) {
                        return subBlocks.filter((subblock) => {
                            return this.blocksPlayed.find(blockPlayed => blockPlayed === subblock.id);
                        }).length === subBlocks.length;
                    }
                }

                return !!this.blocksPlayed.find(blockPlayed => blockPlayed === block.id);
            },

            isActiveBlock(block) {
                const subBlocks = block.subblocks ? block.subblocks : [];

                if (subBlocks.length) {
                    const anySubBlock = subBlocks.some((subblock) => {
                        return this.activeBlockId === subblock.id;
                    });

                    if (anySubBlock) {
                        return true;
                    }
                }

                return this.activeBlockId === block.id;
            },

            getActiveBlock() {
                return this.getBlockById(this.activeBlockId);
            },

            getBlockById(blockId) {
                for (const block of this.ceremony.blocks) {
                    if (block.id === blockId) {
                        return block;
                    }

                    for (const subblock of block.subblocks) {
                        if (subblock.id === blockId) {
                            return subblock;
                        }
                    }
                }

                return null;
            },

            changePlaylist(blockId, playlistBlockId) {
                for (const block of this.ceremony.blocks) {
                    if (block.id !== blockId) {
                        continue;
                    }

                    if (block.type !== 5 || block.type !== 6) {
                        continue;
                    }

                    block.songs_override_id = playlistBlockId;

                    for (const subblock of block.subblocks) {
                        subblock.songs_override_id = playlistBlockId;
                        subblock.parent_block.songs_override_id = playlistBlockId;
                    }
                }
            },

            async stop() {
                try {
                    await this.$http.post(`/api/player/ceremonies/${this.ceremony.id}/stop`);
                    await this.getCeremonyStatus();
                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }
            },

            async reset() {
                try {
                    await this.$http.post(`/api/player/ceremonies/${this.ceremony.id}/reset`);
                    await this.getCeremonyStatus();
                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }
            },

            async checkForCeremonyUpdate() {
                if (!this.ceremony || !this.ceremony.id) {
                    return;
                }

                try {
                    const response = await this.$http.post(`/api/player/ceremonies/${this.ceremony.id}/check-for-update`, {
                        updated_at: this.ceremony.updated_at,
                    });

                    if (response.data.updated) {
                        console.log('Ceremony is updated.. updating local ceremony');
                        await this.getCeremony();
                        await this.getCeremonyStatus();
                    }
                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }
            },

            async playBlockItem(block, initialTransitionTime = undefined, shouldRepeat = undefined, overrideId = undefined, files = undefined) {
                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            if (block.has_manual_action && block.files && block.files.length === 1) {
                                this.currentFileIndex = 0;
                            }

                            await this.$http.post('/api/player/blocks/play', {
                                id: block.id,
                                override_song_id: overrideId || block.songs_override_id,
                                initial_transition_time: initialTransitionTime,
                                should_repeat: shouldRepeat,
                                files,
                                file_id: files?.length ? files[0] : undefined,
                            });
                        } catch (error) {
                            console.log(error);
                            Sentry.captureMessage(error);
                        }

                        break;

                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        const activeBlock = this.getActiveBlock();
                        this.currentFileIndex = null;

                        if (activeBlock) {
                            this.emitAction('BlockFinished', {
                                nextBlock: block ? {
                                    ...block,
                                    ceremony: this.ceremony,
                                } : undefined,
                                block: {
                                    ...activeBlock,
                                    ceremony: this.ceremony,
                                },
                            });
                        }

                        let file = undefined;

                        if (((activeBlock && activeBlock.id !== block.id) || !activeBlock) && block.files && block.files.length === 1 && block.has_manual_action) {
                            this.currentFileIndex = 0;
                            file = block.files[0];

                            console.log('Start first manual file', file);
                        }

                        this.emitAction('BlockStarted', {
                            initial_transition_time: initialTransitionTime,
                            block: {
                                ...block,
                                ceremony: this.ceremony,
                            },
                            file,
                            should_repeat: shouldRepeat,
                            state: 'playing',
                            default_fade_out_time: this.ceremony.company.default_fade_out_time ?? 1900,
                        });

                        break;
                }
            },

            async showBlockFile(block, file) {
                if (this.isTypeLedWall()) {
                    this.currentFileIndex = block.files.findIndex(blockFile => blockFile.id === file.id);

                    await this.playBlockItem(block, 0, true, undefined, [
                        file.id,
                    ]);

                    return;
                }

                switch (this.communication) {
                    case PLAYER_COMMUNICATION.WEBSOCKET:
                        try {
                            await this.$http.post('/api/player/blocks/set-active-file', {
                                id: file.id,
                                block_id: block.id,
                            });
                        } catch (error) {
                            console.log(error);
                            Sentry.captureMessage(error);
                        }

                        break;
                    case PLAYER_COMMUNICATION.IPC:
                    case PLAYER_COMMUNICATION.INLINE:
                        this.emitAction('BlockActiveFileChanged', {
                            file,
                            block: {
                                ...block,
                                ceremony: this.ceremony,
                            },
                        });

                        break;
                }
            },

            async setControllerLivestream(livestream) {
                this.livestream = livestream;

                try {
                    await this.$http.post('/api/player/ceremonies/' + this.ceremony.id + '/set-livestream', {
                        livestream: !!livestream,
                    });
                } catch (error) {
                    console.log(error);
                    Sentry.captureMessage(error);
                }
            },

            closeDetails() {
                this.blockDetails = null;
                this.livestream = null;
            },

            emitAction(action, data) {
                if (this.communication === PLAYER_COMMUNICATION.INLINE) {
                    EventBus.$emit(action, data);
                } else if (this.communication === PLAYER_COMMUNICATION.IPC) {
                    window.dispatchEvent(new CustomEvent('ipc-to-player', {
                        detail: {
                            action,
                            data,
                        },
                    }));

                    // Send event to the controller itself
                    window.dispatchEvent(new CustomEvent('ipc-from-player', {
                        detail: {
                            action,
                            data,
                        },
                    }));
                }
            },

            getBlockMissingFiles(block) {
                if (this.ceremonyDownloadStatus === null) {
                    return [];
                }

                return this.ceremonyDownloadStatus?.missingFiles.filter(file => {
                    return block.subblocks.reduce((blocks, subBlock) => {
                        blocks.push(subBlock.id);
                        return blocks;
                    }, [block.id]).includes(file.blockId);
                });
            },

            isBlockMissingFiles(block) {
                return this.getBlockMissingFiles(block).length >= 1;
            }
        },

    };

</script>

<style lang="scss" scoped>

    .card {
        border-left: 4px solid transparent;
    }

    .subblock-preview {
        margin-top: 30px;
        padding-left: 30px;
        border-left: 4px solid transparent;

        .media-body {
            position: relative;
        }

        .subblock-active {
            position: absolute;
            width: 10px;
            height: 10px;
            border-radius: 5px;
            left: -15px;
            top: 8px;
        }
    }

    .subblock-finished,
    .card-finished {
        border-left: 4px solid #909090;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;

        .media-body {
            opacity: 07;
        }
    }

    .subblock-active,
    .card-active {
        border-left: 4px solid #5cc9a7;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
    }

    .block-comment {
        font-style: italic;
        font-size: 14px;
        line-height: 14px;
        margin-top: 20px;
    }

    @media(max-width: 767px) {
        .action-buttons {
            flex: 100% 1 1;
            width: 100%;
            justify-content: flex-end;
            margin-top: 10px;
        }
    }

</style>
